import { useContext } from 'react';

import { AvatarGroup, Box, Button, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { ImageSizes } from '@sage/ui-components';

import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import HighFiveAdornment from 'src/assets/images/insights-icons/high-five-adornment.svg';
import HighFiveIcon from 'src/assets/images/insights-icons/high-five.png';
import Person from 'src/components/fragments/Person';
import { pluralize } from 'src/utils/intlUtils';

const handKeyframes = keyframes`
    0% {
        transform: scale(0.75) translate(0, 10%);
    }
    25% {
        opacity: 1;
    }
    75% {
        transform: scale(1.2) translate(0, 5%);
    }
    100% {
        transform: scale(1) translate(0, 0);
        opacity: 1;
    }
`;

const burstKeyframes = keyframes`
    0% {
        opacity: 0;
    }
    50% {
        opacity: 0;
    }
    75% {
        opacity: 0.7;
    }
    90% {
        opacity: 0.7;
    }
    100% {
        opacity: 0;
    }
`;

export interface ReceiveHighFiveDrawerProps extends DrawerComponentProps {
    unSeenRecognitions: Array<any>;
    recognitionSenders: Array<any>;
}

function ReceiveHighFiveDrawer(props: ReceiveHighFiveDrawerProps) {
    const { onClose, onNext, unSeenRecognitions = [], recognitionSenders = [] } = props;

    const { trackEvent } = useContext(AnalyticsContext);
    const recognitionsCount = unSeenRecognitions?.length;

    const handleGoBack = () => {
        trackEvent('Go Back Clicked', {
            objectType: ObjectTypes.Button,
            buttonLabel: 'Go Back',
            context: 'Receive High Five Drawer',
        });
        onClose();
    };

    return (
        <>
            <DrawerTitle sx={{ gap: 2, direction: 'row', justifyContent: 'space-between' }}>
                <Stack sx={{ gap: 2, alignItems: 'flex-start' }}>
                    <AvatarGroup max={4}>
                        {recognitionSenders.length > 0 &&
                            recognitionSenders.map((sender) => (
                                <Person
                                    key={sender.id}
                                    id={sender.id}
                                    firstName={sender.firstName}
                                    lastName={sender.lastName}
                                    variant="circular"
                                    size={ImageSizes.Small}
                                />
                            ))}
                    </AvatarGroup>
                    <Stack sx={{ gap: 1 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 'bold',
                            }}
                        >
                            Congratulations!
                        </Typography>
                        <Typography variant="body2">
                            You&apos;ve received{' '}
                            {pluralize(
                                recognitionsCount,
                                `a high five from a colleague.`,
                                `${recognitionsCount} high fives from colleagues.`,
                                false
                            )}
                        </Typography>
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        position: 'relative',
                        minWidth: 100,
                        height: 100,
                    }}
                >
                    <Box
                        component="img"
                        src={HighFiveAdornment}
                        alt=""
                        sx={{
                            opacity: 0,
                            position: 'absolute',
                            top: '-15%',
                            left: '2%',
                            animation: `${burstKeyframes} 0.75s ease-out 0.5s 1`,
                        }}
                    />
                    <Box
                        component="img"
                        src={HighFiveIcon}
                        alt=""
                        sx={{
                            position: 'absolute',
                            top: '5%',
                            left: '5%',
                            opacity: 0,
                            animation: `${handKeyframes} 0.75s ease-out 0.5s forwards`,
                        }}
                    />
                </Box>
            </DrawerTitle>
            <DrawerFooter sx={{ gap: 2.5 }}>
                <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    disableElevation
                    onClick={onNext}
                >
                    View Recognitions
                </Button>

                <Button color="primary" disableElevation fullWidth onClick={handleGoBack}>
                    Later
                </Button>
            </DrawerFooter>
        </>
    );
}

export default ReceiveHighFiveDrawer;
